/**
 * CreateCompanyGroupModal Component
 *
 * Модальное окно для создания группы компаний.
 * Только поле "Название" обязательно.
 */

import React, { useState } from 'react';
import {
  Modal,
  TextInput,
  Button,
  Group,
  Stack,
  Text,
  LoadingOverlay
} from '@mantine/core';
import { IconBuilding, IconPlus } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import tokens from '../../theme/designTokens';


export function CreateCompanyGroupModal({ opened, onClose, onSuccess }) {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    // Валидация
    if (!name.trim()) {
      setError('Введите название группы компаний');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/company-groups', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ name: name.trim() }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Не удалось создать группу компаний');
      }

      const createdGroup = await response.json();

      notifications.show({
        title: 'Группа создана',
        message: `Группа компаний "${name}" успешно создана`,
        color: 'green',
      });

      // Сбрасываем форму
      setName('');
      onClose();

      // Вызываем callback для обновления списка
      if (onSuccess) {
        onSuccess(createdGroup);
      }
    } catch (err) {
      console.error('Error creating company group:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName('');
    setError('');
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={
        <Group gap="xs">
          <IconBuilding size={20} color={tokens.colors.brand.primary} />
          <Text fw={600}>Создать группу компаний</Text>
        </Group>
      }
      size="md"
      centered
    >
      <LoadingOverlay visible={loading} />

      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Группа компаний объединяет несколько организаций под общим брендом или владением.
          После создания вы сможете добавить организации в группу.
        </Text>

        <TextInput
          label="Название группы"
          placeholder="Например: ГК Рестораны Москвы"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setError('');
          }}
          error={error}
          required
          data-autofocus
        />

        <Group justify="flex-end" mt="md">
          <Button variant="default" onClick={handleClose}>
            Отмена
          </Button>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleSubmit}
            loading={loading}
          >
            Создать
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export default CreateCompanyGroupModal;
